import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Skull, Plus, Trash2, ExternalLink, Pencil } from "lucide-react";
import { AutoLink } from "../components/AutoLink";

const EMPTY = { name: "", source: "", reason: "", points: 0 };

export default function HallOfShame() {
  const { user } = useAuth();
  const { t, translateError } = useLanguage();
  const isAdmin = user && user.role === "admin";

  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    const url = isAdmin ? "/admin/hall-of-shame" : "/hall-of-shame";
    api.get(url)
      .then(({ data }) => setRows(data))
      .catch(() => { toast.error(t("hos.loadError")); setRows([]); });
  };
  useEffect(() => { if (user !== undefined) load(); /* eslint-disable-next-line */ }, [isAdmin]);

  const openAdd = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditId(r.id);
    setForm({ name: r.name, source: r.source, reason: r.reason, points: r.points || 0 });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.source.trim() || !form.reason.trim()) return toast.error(t("hos.fillAll"));
    const payload = {
      name: form.name.trim(),
      source: form.source.trim(),
      reason: form.reason.trim(),
      points: parseInt(form.points, 10) || 0,
    };
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/admin/hall-of-shame/${editId}`, payload);
        toast.success(t("hos.saved"));
      } else {
        await api.post("/admin/hall-of-shame", payload);
        toast.success(t("hos.added"));
      }
      setOpen(false);
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(translateError(formatApiError(e.response?.data?.detail)));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/hall-of-shame/${id}`);
      toast.success(t("hos.deleted"));
      load();
    } catch (e) {
      toast.error(translateError(formatApiError(e.response?.data?.detail)));
    }
  };

  const isUrl = (s) => /^https?:\/\//i.test(s);
  const domainOf = (s) => {
    try { return new URL(s).hostname.replace(/^www\./, ""); } catch { return s; }
  };

  return (
    <main className="relative z-10 mx-auto max-w-4xl px-6 py-14 md:px-10" data-testid="hall-of-shame-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <Skull className="h-6 w-6 text-lie" />
            <span className="font-mono text-xs uppercase tracking-widest text-lie">{t("hos.eyebrow")}</span>
          </div>
          <h1 className="mt-3 font-heading text-4xl font-black tracking-tighter sm:text-6xl">{t("hos.title")}</h1>
        </div>
        {isAdmin && (
          <Button onClick={openAdd} data-testid="open-add-shame" className="rounded-md bg-primary font-bold uppercase tracking-wider hover:bg-[#FF645A]">
            <Plus className="mr-2 h-4 w-4" /> {t("hos.add")}
          </Button>
        )}
      </div>

      <div className="mt-6 space-y-4 border-l-2 border-lie/50 bg-lie/5 p-6">
        <p className="text-base leading-relaxed text-foreground/90">{t("hos.intro")}</p>
        <p className="text-sm leading-relaxed text-muted-foreground">{t("hos.body")}</p>
        <p className="text-sm leading-relaxed text-muted-foreground">{t("hos.conclusion")}</p>
      </div>

      {!rows ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-8 border border-dashed border-white/15 p-10 text-center text-muted-foreground">{t("hos.empty")}</p>
      ) : (
        <div className="mt-8 space-y-4">
          {rows.map((r, i) => (
            <div key={r.id} data-testid={`shame-card-${r.id}`} className="border border-white/10 bg-card p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-lie/30 font-heading text-lg font-black text-lie">{i + 1}</span>
                  <div>
                    <h3 className="font-heading text-2xl font-black leading-tight">{r.name}</h3>
                    <p className="mt-1 font-mono text-sm font-bold text-lie" data-testid={`shame-points-${r.id}`}>{r.points} {t("hos.pointsLabel")}</p>
                    {r.pending && (
                      <div className="mt-2" data-testid={`shame-pending-${r.id}`}>
                        <span className="inline-block border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-amber-400">{t("hos.pendingBadge")}</span>
                        {r.publish_at && (
                          <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            {t("hos.visibleFrom", { when: new Date(r.publish_at).toLocaleString() })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-3">
                    <button onClick={() => openEdit(r)} data-testid={`edit-shame-${r.id}`} className="text-muted-foreground transition-colors hover:text-primary" aria-label="edit">
                      <Pencil className="h-5 w-5" />
                    </button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`delete-shame-${r.id}`} className="text-muted-foreground transition-colors hover:text-lie" aria-label="delete">
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="border-white/10 bg-card">
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("hos.deleteTitle")}</AlertDialogTitle>
                          <AlertDialogDescription>{t("hos.deleteDesc", { name: r.name })}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="border-white/15 bg-transparent">{t("common.cancel")}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(r.id)} data-testid={`confirm-delete-shame-${r.id}`} className="bg-lie hover:bg-lie/80">{t("adminUser.delete")}</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
              <p className="mt-3 leading-relaxed text-foreground/85"><AutoLink text={r.reason} /></p>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                {t("hos.sourceLabel")}:{" "}
                {isUrl(r.source) ? (
                  <a href={r.source} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                    {domainOf(r.source)} <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="text-foreground/70">{r.source}</span>
                )}
              </p>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg border-white/10 bg-card" data-testid="shame-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black">{editId ? t("hos.editTitle") : t("hos.addTitle")}</DialogTitle>
          </DialogHeader>
          {!editId && (
            <p className="border-l-2 border-amber-500/50 bg-amber-500/5 px-3 py-2 text-xs text-muted-foreground">{t("hos.delayInfo")}</p>
          )}
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("hos.name")}</Label>
              <Input data-testid="shame-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={140} className="border-white/15 bg-background" placeholder={t("hos.namePlaceholder")} />
            </div>
            <div>
              <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("hos.source")}</Label>
              <Input data-testid="shame-source-input" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} maxLength={500} className="border-white/15 bg-background" placeholder={t("hos.sourcePlaceholder")} />
            </div>
            <div>
              <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("hos.reason")}</Label>
              <Textarea data-testid="shame-reason-input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={4} maxLength={2000} className="border-white/15 bg-background" placeholder={t("hos.reasonPlaceholder")} />
            </div>
            <div>
              <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("hos.points")}</Label>
              <Input type="number" min={0} data-testid="shame-points-input" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} className="border-white/15 bg-background" placeholder={t("hos.pointsPlaceholder")} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/15 bg-transparent">{t("common.cancel")}</Button>
            <Button onClick={save} disabled={saving} data-testid="confirm-save-shame" className="rounded-md bg-primary font-bold uppercase tracking-wider hover:bg-[#FF645A]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editId ? t("hos.edit") : t("hos.add"))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
